import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { 
  Calendar,
  ArrowRight,
  BookOpen,
  MessageSquare,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component for translated article card
const TranslatedArticleCard = ({ article, language, translateContent, formatDate, readMore }) => {
  const [translatedTitle, setTranslatedTitle] = useState(article.title);
  const [translatedContent, setTranslatedContent] = useState(article.content);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const translateArticle = async () => {
      if (language === 'ro') {
        setTranslatedTitle(article.title);
        setTranslatedContent(article.content);
        return;
      }

      setIsTranslating(true);
      try {
        const [title, content] = await Promise.all([
          translateContent(article.title, 'article_title', `${article.id}_title`),
          translateContent(article.content, 'article_content', `${article.id}_content`)
        ]);
        setTranslatedTitle(title);
        setTranslatedContent(content);
      } catch (error) {
        console.error('Translation error:', error);
      } finally {
        setIsTranslating(false);
      }
    };

    translateArticle();
  }, [article, language, translateContent]);

  const getExcerpt = (content, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  return (
    <Link
      to={`/jurnal/${article.id}`}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
    >
      {article.image_url && (
        <div className="aspect-video overflow-hidden">
          <img
            src={article.image_url}
            alt={translatedTitle}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(article.created_at)}
          </span>
          {article.comments_count > 0 && (
            <span className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              {article.comments_count}
            </span>
          )}
        </div>
        
        {isTranslating ? (
          <div className="flex items-center gap-2 mb-3">
            <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
            <span className="text-slate-400 text-sm">Translating...</span>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors">
              {translatedTitle}
            </h2>
            <p className="text-slate-600 mb-4 line-clamp-3">
              {getExcerpt(translatedContent)}
            </p>
          </>
        )}
        
        <span className="inline-flex items-center gap-2 text-sky-600 font-medium group-hover:gap-3 transition-all">
          {readMore}
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
};

const JournalPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language, translateContent } = useLanguage();

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/articles`);
        setArticles(response.data || []);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'ro' ? 'ro-RO' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getExcerpt = (content, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  const pageTitle = language === 'ro' ? 'Jurnal' : language === 'de' ? 'Tagebuch' : 'Journal';
  const pageSubtitle = language === 'ro' 
    ? 'Experiențe, gânduri și povești din călătoria mea cu LifeWave'
    : language === 'de' 
    ? 'Erfahrungen, Gedanken und Geschichten aus meiner LifeWave-Reise'
    : 'Experiences, thoughts and stories from my LifeWave journey';
  const readMore = language === 'ro' ? 'Citește mai mult' : language === 'de' ? 'Mehr lesen' : 'Read more';
  const noArticles = language === 'ro' ? 'Nu există articole încă' : language === 'de' ? 'Noch keine Artikel' : 'No articles yet';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="journal-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 via-white to-orange-50 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            {pageTitle}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {pageTitle}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {pageSubtitle}
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          {articles.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">{noArticles}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map(article => (
                <TranslatedArticleCard
                  key={article.id}
                  article={article}
                  language={language}
                  translateContent={translateContent}
                  formatDate={formatDate}
                  readMore={readMore}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default JournalPage;
